import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
    AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Plus, FolderKanban, Trash2, FileText, Printer, Beaker, ClipboardList } from "lucide-react";

export default function ProjectWorkspace({ onChange }) {
    const [projects, setProjects] = useState([]);
    const [regions, setRegions] = useState([]);
    const [selected, setSelected] = useState(null);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", region_id: "", objective: "" });
    const [finding, setFinding] = useState("");
    const [featureType, setFeatureType] = useState("observation");
    const [confirmDelete, setConfirmDelete] = useState(null);

    const load = async () => {
        const { data } = await api.get("/projects");
        setProjects(data);
        if (selected) {
            const fresh = data.find((p) => p.id === selected.id);
            setSelected(fresh || null);
        } else if (data.length) {
            setSelected(data[0]);
        }
    };

    useEffect(() => {
        api.get("/regions").then(({ data }) => setRegions(data));
        load();
        // eslint-disable-next-line
    }, []);

    const createProject = async () => {
        if (!form.name.trim()) { toast.error("Project name required"); return; }
        try {
            const { data } = await api.post("/projects", form);
            setProjects((p) => [data, ...p]);
            setSelected(data);
            setOpen(false);
            setForm({ name: "", region_id: "", objective: "" });
            toast.success("Project created");
            onChange && onChange();
        } catch (e) {
            toast.error("Could not create project");
        }
    };

    const deleteProject = async (id) => {
        await api.delete(`/projects/${id}`);
        const rest = projects.filter((p) => p.id !== id);
        setProjects(rest);
        setSelected(rest[0] || null);
        setConfirmDelete(null);
        toast.success("Project deleted");
        onChange && onChange();
    };

    const addFinding = async () => {
        if (!finding.trim() || !selected) return;
        const { data } = await api.post(`/projects/${selected.id}/findings`, { text: finding, feature_type: featureType });
        setSelected((s) => ({ ...s, findings: [...(s.findings || []), data] }));
        setFinding("");
        toast.success("Finding logged");
    };

    const deleteFinding = async (fid) => {
        await api.delete(`/projects/${selected.id}/findings/${fid}`);
        setSelected((s) => ({ ...s, findings: s.findings.filter((f) => f.id !== fid) }));
    };

    const regionName = (id) => regions.find((r) => r.id === id)?.name || "Unassigned region";

    const exportReport = () => {
        if (!selected) return;
        const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
        const region = regions.find((r) => r.id === selected.region_id);
        const w = window.open("", "_blank");
        w.document.write(`
            <html><head><title>${esc(selected.name)} — Mars Research Report</title>
            <style>
                body{font-family:Georgia,serif;max-width:760px;margin:40px auto;padding:0 24px;color:#1a1a1a;line-height:1.6}
                h1{font-size:26px;border-bottom:3px solid #ff6b35;padding-bottom:8px}
                h2{font-size:18px;color:#c2410c;margin-top:28px}
                .meta{color:#666;font-size:13px}
                .finding{border-left:3px solid #ff6b35;padding:6px 12px;margin:10px 0;background:#faf7f5}
                .tag{display:inline-block;background:#ff6b35;color:#fff;font-size:10px;padding:2px 8px;border-radius:10px;text-transform:uppercase;letter-spacing:1px}
                table{width:100%;border-collapse:collapse;margin-top:10px;font-size:13px}
                td,th{border:1px solid #ddd;padding:6px 10px;text-align:left}
            </style></head><body>
            <h1>${esc(selected.name)}</h1>
            <p class="meta">ARES GEO-ICE Research Report · Generated ${new Date().toLocaleString()}</p>
            <h2>Objective</h2><p>${esc(selected.objective) || "—"}</p>
            <h2>Study Region</h2>
            ${region ? `<table>
                <tr><th>Region</th><td>${esc(region.name)} (${esc(region.quadrant)})</td></tr>
                <tr><th>Coordinates</th><td>${region.lat}°, ${region.lon}°E</td></tr>
                <tr><th>Elevation</th><td>${region.elevation_m.toLocaleString()} m</td></tr>
                <tr><th>Ice probability</th><td>${Math.round(region.ice_probability*100)}%</td></tr>
                <tr><th>Mean slope</th><td>${region.slope_deg}°</td></tr>
                <tr><th>Water evidence</th><td>${esc(region.water_evidence)}</td></tr>
            </table>` : "<p>No region assigned.</p>"}
            <h2>Findings (${(selected.findings||[]).length})</h2>
            ${(selected.findings||[]).map(f=>`<div class="finding"><span class="tag">${esc(f.feature_type)}</span><p>${esc(f.text)}</p></div>`).join("") || "<p>No findings logged.</p>"}
            <h2>Map Annotations (${(selected.annotations||[]).length})</h2>
            ${(selected.annotations||[]).length ? `<table>
                <tr><th>Label</th><th>Category</th><th>Coordinates</th><th>Note</th></tr>
                ${(selected.annotations||[]).map(a=>`<tr><td>${esc(a.label)}</td><td>${esc(a.category||"observation")}</td><td>${a.lat.toFixed(2)}, ${a.lon.toFixed(2)}</td><td>${esc(a.note)||"—"}</td></tr>`).join("")}
            </table>` : "<p>No map annotations.</p>"}
            </body></html>`);
        w.document.close();
        w.focus();
        w.print();
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-5">
            {/* Project list */}
            <aside className="rounded-xl border border-ares-border bg-ares-dark/90 p-4">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="font-heading text-lg font-medium flex items-center gap-2"><FolderKanban className="w-5 h-5 text-ares-orange" /> Projects</h2>
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button size="icon" className="h-8 w-8 bg-ares-orange hover:bg-[#e55a2b] text-white" data-testid="new-project-btn"><Plus className="w-4 h-4" /></Button>
                        </DialogTrigger>
                        <DialogContent className="bg-ares-dark border-ares-border text-ares-bright">
                            <DialogHeader><DialogTitle className="font-heading">New Research Project</DialogTitle></DialogHeader>
                            <div className="space-y-3 py-2">
                                <Input placeholder="Project name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                                    className="bg-ares-panel border-ares-border" data-testid="project-name-input" />
                                <Select value={form.region_id} onValueChange={(v) => setForm((f) => ({ ...f, region_id: v }))}>
                                    <SelectTrigger className="bg-ares-panel border-ares-border" data-testid="project-region-select"><SelectValue placeholder="Study region (optional)" /></SelectTrigger>
                                    <SelectContent className="bg-ares-panel border-ares-border">
                                        {regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                <Textarea placeholder="Research objective…" value={form.objective} onChange={(e) => setForm((f) => ({ ...f, objective: e.target.value }))}
                                    className="bg-ares-panel border-ares-border resize-none h-24" data-testid="project-objective-input" />
                            </div>
                            <DialogFooter>
                                <Button onClick={createProject} className="bg-ares-orange hover:bg-[#e55a2b] text-white" data-testid="create-project-confirm-btn">Create Project</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>

                <div className="space-y-2">
                    {projects.length === 0 && <p className="text-sm text-ares-muted text-center py-8">No projects yet. Create one to start.</p>}
                    {projects.map((p) => (
                        <button key={p.id} onClick={() => setSelected(p)} data-testid={`project-item-${p.id}`}
                            className={`w-full text-left rounded-lg border p-3 transition-colors ${selected?.id === p.id ? "border-ares-orange/50 bg-ares-orange/10" : "border-ares-border bg-ares-panel hover:border-ares-orange/30"}`}>
                            <p className="font-medium text-sm truncate">{p.name}</p>
                            <p className="font-mono text-[10px] uppercase tracking-wider text-ares-muted mt-0.5">{regionName(p.region_id)} · {(p.findings || []).length} findings</p>
                        </button>
                    ))}
                </div>
            </aside>

            {/* Project detail */}
            <section className="rounded-xl border border-ares-border bg-ares-dark/90 p-5" data-testid="project-detail">
                {!selected ? (
                    <div className="flex flex-col items-center justify-center h-[360px] text-center text-ares-muted">
                        <ClipboardList className="w-10 h-10 mb-3 opacity-40" />
                        <p className="text-sm">Select or create a project to log findings and build a report.</p>
                    </div>
                ) : (
                    <>
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div>
                                <p className="font-mono text-[10px] uppercase tracking-widest text-ares-orange">{regionName(selected.region_id)}</p>
                                <h2 className="font-heading text-2xl font-semibold mt-1">{selected.name}</h2>
                                {selected.objective && <p className="text-sm text-ares-muted mt-2 max-w-xl">{selected.objective}</p>}
                            </div>
                            <div className="flex gap-2">
                                <Button variant="outline" onClick={exportReport} className="border-ares-border bg-transparent hover:bg-ares-panel" data-testid="report-export-pdf-btn">
                                    <Printer className="w-4 h-4 mr-2" /> Export Report
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => setConfirmDelete(selected)} className="text-ares-muted hover:text-destructive hover:bg-destructive/10" data-testid="delete-project-btn">
                                    <Trash2 className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>

                        {/* Add finding */}
                        <div className="mt-6 rounded-lg border border-ares-border bg-ares-panel p-4">
                            <p className="font-mono text-[10px] uppercase tracking-widest text-ares-muted mb-2 flex items-center gap-1.5"><Beaker className="w-3.5 h-3.5" /> Log a finding</p>
                            <Textarea value={finding} onChange={(e) => setFinding(e.target.value)} placeholder="Describe an observation, measurement or geological interpretation…"
                                className="bg-ares-dark border-ares-border resize-none h-20 text-sm" data-testid="finding-input" />
                            <div className="flex gap-2 mt-3">
                                <Select value={featureType} onValueChange={setFeatureType}>
                                    <SelectTrigger className="bg-ares-dark border-ares-border w-44" data-testid="finding-type-select"><SelectValue /></SelectTrigger>
                                    <SelectContent className="bg-ares-panel border-ares-border">
                                        <SelectItem value="observation">Observation</SelectItem>
                                        <SelectItem value="measurement">Measurement</SelectItem>
                                        <SelectItem value="interpretation">Interpretation</SelectItem>
                                        <SelectItem value="ai-classification">AI Classification</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Button onClick={addFinding} disabled={!finding.trim()} className="bg-ares-orange hover:bg-[#e55a2b] text-white flex-1" data-testid="add-finding-btn">
                                    <Plus className="w-4 h-4 mr-1" /> Add Finding
                                </Button>
                            </div>
                        </div>

                        {/* Findings list */}
                        <div className="mt-5 space-y-2.5">
                            <p className="font-mono text-[10px] uppercase tracking-widest text-ares-muted flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Logged findings ({(selected.findings || []).length})</p>
                            {(selected.findings || []).length === 0 && <p className="text-sm text-ares-muted py-4">No findings logged yet.</p>}
                            {(selected.findings || []).map((f) => (
                                <div key={f.id} className="group rounded-lg border border-ares-border bg-ares-panel p-3 flex items-start justify-between gap-3" data-testid={`finding-${f.id}`}>
                                    <div>
                                        <span className="inline-block text-[10px] uppercase tracking-wider font-mono rounded bg-ares-orange/15 text-ares-orange px-1.5 py-0.5 mb-1.5">{f.feature_type}</span>
                                        <p className="text-sm text-ares-bright/90">{f.text}</p>
                                    </div>
                                    <button onClick={() => deleteFinding(f.id)} className="text-ares-muted hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity shrink-0" data-testid={`delete-finding-${f.id}`}>
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </section>

            <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <AlertDialogContent className="bg-ares-dark border-ares-border text-ares-bright">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-heading">Delete this project?</AlertDialogTitle>
                        <AlertDialogDescription className="text-ares-muted">
                            "{confirmDelete?.name}" and all its findings and map annotations will be permanently removed. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="bg-ares-panel border-ares-border hover:bg-ares-border text-ares-bright" data-testid="cancel-delete-project-btn">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => deleteProject(confirmDelete.id)} className="bg-destructive hover:bg-destructive/90 text-white" data-testid="confirm-delete-project-btn">Delete Project</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
